import React, { useState, useRef } from "react";
import { Upload, ArrowLeft, Sparkles } from "lucide-react";
import Papa from "papaparse";

function App() {
  const [page, setPage] = useState("draw");
  const [participants, setParticipants] = useState([]);
  const [winner, setWinner] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnWinners, setDrawnWinners] = useState(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem("drawnWinners");
    return saved ? JSON.parse(saved) : [];
  });
  const [drawInterval, setDrawInterval] = useState(null);
  const MAX_WINNERS = 3; // Limit pemenang maksimal
  const audioContextRef = useRef(null);
  const drumrollIntervalRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        setParticipants(results.data);
        setWinner(null);
        setDrawnWinners([]);
        setPage("draw");
      },
      error: (error) => {
        alert("Error parsing CSV: " + error.message);
      },
    });
  };

  const playDrumroll = () => {
    // Create audio context
    audioContextRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Function to play continuous drumroll sound
    const playDrumSound = () => {
      if (!audioContextRef.current) return;

      const audioContext = audioContextRef.current;
      const time = audioContext.currentTime;

      // Create 30 quick drum hits for continuous effect
      for (let i = 0; i < 30; i++) {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.connect(gain);
        gain.connect(audioContext.destination);

        osc.frequency.value = 80 + Math.random() * 20;
        osc.type = "sawtooth";

        const startTime = time + i * 0.033; // 33ms between each hit
        gain.gain.setValueAtTime(0.3, startTime); // Increased from 0.1 to 0.3
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.05);

        osc.start(startTime);
        osc.stop(startTime + 0.05);
      }
    };

    // Play initial sound
    playDrumSound();

    // Continue playing every 1 second until stopped
    drumrollIntervalRef.current = setInterval(() => {
      playDrumSound();
    }, 1000);
  };

  const stopDrumroll = () => {
    // Clear the drumroll interval
    if (drumrollIntervalRef.current) {
      clearInterval(drumrollIntervalRef.current);
      drumrollIntervalRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const playWinnerSound = () => {
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.5];

    notes.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();

      osc.connect(gain);
      gain.connect(audioContext.destination);

      osc.frequency.value = freq;
      osc.type = "sine";

      const startTime = audioContext.currentTime + i * 0.1;
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 1);

      osc.start(startTime);
      osc.stop(startTime + 1);
    });
  };

  const drawWinner = () => {
    if (participants.length === 0) {
      alert("Silakan upload file CSV terlebih dahulu!");
      setPage("upload");
      return;
    }

    // Cek apakah sudah mencapai limit pemenang
    if (drawnWinners.length >= MAX_WINNERS) {
      alert(`Pengundian selesai! Sudah ada ${MAX_WINNERS} pemenang.`);
      return;
    }

    const availableParticipants = participants.filter(
      (p) => !drawnWinners.some((w) => JSON.stringify(w) === JSON.stringify(p))
    );

    if (availableParticipants.length === 0) {
      alert(
        "Semua peserta sudah menang! Silakan reset list atau upload data baru."
      );
      return;
    }

    setIsDrawing(true);
    setWinner(null);
    playDrumroll();

    const interval = setInterval(() => {
      const randomIndex = Math.floor(
        Math.random() * availableParticipants.length
      );
      setWinner(availableParticipants[randomIndex]);
    }, 100);

    setDrawInterval(interval);
  };

  const stopDrawing = () => {
    if (drawInterval && isDrawing) {
      clearInterval(drawInterval);
      setDrawInterval(null);
      setIsDrawing(false);

      // Stop the continuous drumroll sound
      stopDrumroll();

      const availableParticipants = participants.filter(
        (p) =>
          !drawnWinners.some((w) => JSON.stringify(w) === JSON.stringify(p))
      );

      const finalIndex = Math.floor(
        Math.random() * availableParticipants.length
      );
      const finalWinner = availableParticipants[finalIndex];

      setWinner(finalWinner);
      setDrawnWinners((prev) => [...prev, finalWinner]);
      playWinnerSound();

      // Cek apakah sudah mencapai limit setelah menambah pemenang
      if (drawnWinners.length + 1 >= MAX_WINNERS) {
        setTimeout(() => {
          alert(
            `🎉 Pengundian selesai! ${MAX_WINNERS} pemenang telah terpilih!`
          );
        }, 1000);
      }
    }
  };

  const reset = () => {
    setWinner(null);
  };

  // Save drawnWinners to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem("drawnWinners", JSON.stringify(drawnWinners));
  }, [drawnWinners]);

  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        if (isDrawing) {
          stopDrawing();
        } else {
          drawWinner();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isDrawing, drawInterval, participants, drawnWinners]);

  if (page === "upload") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-300 via-blue-400 to-purple-500 p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <button
              onClick={() => setPage("draw")}
              className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft size={20} />
              Kembali
            </button>

            <div className="text-center mb-8">
              <Upload className="mx-auto text-purple-500 mb-4" size={64} />
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Upload Data Pelanggan
              </h1>
              <p className="text-gray-600">
                Upload file CSV untuk undian berhadiah
              </p>
            </div>

            <label className="flex flex-col items-center justify-center w-full h-64 border-4 border-dashed border-purple-300 rounded-2xl cursor-pointer bg-purple-50 hover:bg-purple-100 transition-all hover:border-purple-400">
              <Upload className="text-purple-500 mb-4" size={48} />
              <span className="text-lg font-semibold text-gray-700 mb-2">
                Klik untuk upload CSV
              </span>
              <span className="text-sm text-gray-500">
                Format: Nomor Sambungan, Nama, Alamat, Telepon, <br></br>Nama
                Pemilik Sambungan, Alamat Pemilik Sambungan
              </span>
              {participants.length > 0 && (
                <span className="mt-4 text-green-600 font-semibold">
                  ✓ {participants.length} pelanggan telah dimuat
                </span>
              )}
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <div className="mt-8 bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-3">Format CSV:</h3>
              <div className="bg-white rounded p-3 text-sm font-mono text-gray-700">
                nosbg,nama,alamat,telepon,nama_pemilik_sambungan,<br></br>
                alamat_pemilik_sambungan
                <br />
                001234,Budi Santoso,Jl. Merdeka No. 10,081234567890, Budi
                Santoso,Jl. Merdeka No. 10
                <br />
                001235,Siti Nurhaliza,Jl. Sudirman No. 15,082345678901, Arya
                Wijaya,Jl. Gajah Mada No. 15
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-300 via-blue-400 to-purple-500 p-8 flex items-center justify-center relative overflow-hidden">
      {isDrawing && (
        <>
          {[...Array(20)].map((_, i) => (
            <Sparkles
              key={i}
              className="absolute text-white animate-pulse"
              size={24}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`,
              }}
            />
          ))}
        </>
      )}

      <div className="max-w-5xl w-full relative z-10">
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <div className="text-center mb-12">
            <img
              src="/logo_pdam.png"
              alt="PDAM Tirta Hita Buleleng"
              className="mx-auto mb-4 h-36 object-contain animate-bounce"
            />
            <h1 className="text-5xl font-bold text-gray-800 mb-8">
              Pengundian Survey Kepuasan Pelanggan Tahun 2025
            </h1>
            <p className="text-xl text-gray-600">
              {isDrawing
                ? "⌨️ Tekan SPASI untuk berhenti!"
                : "⌨️ Tekan SPASI untuk mulai mengundi!"}
            </p>
          </div>

          <div className="text-center mb-8">
            <div className="inline-block bg-blue-100 rounded-full px-8 py-3">
              <span className="text-2xl font-bold text-blue-800">
                {drawnWinners.length} / {MAX_WINNERS} Pemenang Terpilih
              </span>
            </div>
          </div>

          {winner && (
            <div
              className={`mb-8 transform transition-all duration-500 ${
                isDrawing ? "scale-95 opacity-70" : "scale-100 opacity-100"
              }`}
            >
              <div className="bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 rounded-3xl p-8 shadow-xl">
                <h2 className="text-center text-3xl font-bold text-white mb-6">
                  {isDrawing
                    ? "🎲 MENGUNDI..."
                    : "🎉 SELAMAT PEMENANGNYA ADALAH... 🎉"}
                </h2>
                <div className="bg-white rounded-2xl p-8 space-y-4">
                  <div className="border-b-2 border-gray-200 pb-4">
                    <div className="text-sm text-gray-500 mb-1">
                      Nomor Sambungan
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                      {winner["nosbg"] || winner["nosbg"] || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Nama </div>
                      <div className="text-xl font-semibold text-gray-800">
                        {winner["nama"] || winner["Nama"] || "-"}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Nomor HP</div>
                      <div className="text-xl font-semibold text-gray-800">
                        {winner["telepon"] || winner["Telepon"] || "-"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Alamat</div>
                    <div className="text-lg text-gray-800">
                      {winner["alamat"] || winner["Alamat"] || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 mb-8">
            <button
              onClick={drawWinner}
              disabled={isDrawing || drawnWinners.length >= MAX_WINNERS}
              className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-6 px-8 rounded-2xl text-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {drawnWinners.length >= MAX_WINNERS
                ? "✅ PENGUNDIAN SELESAI"
                : isDrawing
                ? "🎲 MENGUNDI..."
                : "🎁 UNDI PEMENANG!"}
            </button>
          </div>

          <div className="flex gap-4">
            <button
              onClick={reset}
              disabled={
                !winner || isDrawing || drawnWinners.length >= MAX_WINNERS
              }
              className="flex-1 bg-cyan-500 text-white font-bold py-4 px-6 rounded-xl hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Undi Lagi
            </button>
            <button
              onClick={() => setPage("upload")}
              className="flex-1 bg-indigo-500 text-white font-bold py-4 px-6 rounded-xl hover:bg-indigo-600 transition flex items-center justify-center gap-2"
            >
              <Upload size={20} />
              Upload Data Baru
            </button>
          </div>
        </div>

        {drawnWinners.length > 0 && (
          <div className="mt-8 bg-white rounded-3xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-blue-700">
                🏆 Daftar Pemenang ({drawnWinners.length}/{MAX_WINNERS})
              </h3>
              <button
                onClick={() => {
                  setDrawnWinners([]);
                  localStorage.removeItem("drawnWinners");
                }}
                className="bg-red-500 text-white font-bold py-2 px-6 rounded-lg hover:bg-red-600 transition"
              >
                Reset List
              </button>
            </div>
            <div className="space-y-3">
              {drawnWinners.map((w, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-blue-500 text-white font-bold px-3 py-1 rounded-full text-sm">
                          #{idx + 1}
                        </span>
                        <span className="text-xl font-bold text-blue-700">
                          {w["nosbg"] || w["Nosbg"]}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <div className="text-xs text-gray-500">Nama</div>
                          <div className="text-sm font-semibold text-gray-800">
                            {w["nama"] || w["Nama"]}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Nomor HP</div>
                          <div className="text-sm font-semibold text-gray-800">
                            {w["telepon"] || w["Telepon"]}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Alamat</div>
                          <div className="text-sm text-gray-800">
                            {w["alamat"] || w["Alamat"]}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <div className="text-xs text-gray-500">
                            Nama Pemilik Sambungan
                          </div>
                          <div className="text-sm font-semibold text-gray-800">
                            {w["nama_pemilik_sambungan"] ||
                              w["Nama_Pemilik_Sambungan"] ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">
                            Alamat Pemilik Sambungan
                          </div>
                          <div className="text-sm text-gray-800">
                            {w["alamat_pemilik_sambungan"] ||
                              w["Alamat_Pemilik_Sambungan"] ||
                              "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
